import CurrencyInput from 'components/base/currency-input';
import SelectDropdown from 'components/base/select-dropdown';
import { ProcurementItemType } from 'models/procurement.model';
import React, { useEffect, useMemo, useState } from 'react';
import { DeleteIcon } from 'cokitchen-icons';

export interface ReturnProcurementItemType {
  batch_no: string;
  quantityReturned: number;
  id: string;
}

interface ProcurementRequestItemReturnProps {
  item: ProcurementItemType;
  onItemChange: (updatedItem: { batch_no: string; quantityReturned: number; id: string }[]) => void;
}

const ProcurementRequestItemReturn = ({
  item,
  onItemChange
}: ProcurementRequestItemReturnProps) => {
  const [selectedBatches, setSelectedBatches] = useState<ReturnProcurementItemType[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [inputValues, setInputValues] = useState<Record<string, string>>({});

  const addNewBatch = () => {
    setSelectedBatches([...selectedBatches, { batch_no: '', quantityReturned: 0, id: '' }]);
  };

  useEffect(() => {
    const initBatch = {
      batch_no: `Batch ${item?.batches?.[0]?.batch_no}`,
      quantityReturned: 0,
      id: item?.id
    };
    setSelectedBatches([initBatch]);
    setErrors({});
    setInputValues({});
    onItemChange([initBatch]);
  }, [item]);

  const updateReturnedItem = (index: number, updates: Partial<ReturnProcurementItemType>) => {
    const updatedItems = [...selectedBatches];
    updatedItems[index] = {
      ...updatedItems[index],
      ...updates
    };
    setSelectedBatches(updatedItems);
    onItemChange(updatedItems);
  };

  const removeBatch = (index: number) => {
    const batchNo = selectedBatches[index].batch_no;
    const updatedItems = selectedBatches.filter((_, idx) => idx !== index);
    setSelectedBatches(updatedItems);
    setErrors((prev) => {
      const next = { ...prev };
      delete next[batchNo];
      return next;
    });
    setInputValues((prev) => {
      const next = { ...prev };
      delete next[batchNo];
      return next;
    });
    onItemChange(updatedItems);
  };

  const allBatchesOptions = useMemo(() => {
    if (!item?.batches) return [];
    return item.batches.map((batch) => ({
      name: `Batch ${batch.batch_no}`,
      id: item.id,
      price: `${batch.quantity} items`
    }));
  }, [item]);

  const getFilteredOptions = useMemo(
    () => (index: number) => {
      return allBatchesOptions.filter(
        (option) =>
          !selectedBatches.some((selected, i) => selected.batch_no === option.name && i !== index)
      );
    },
    [allBatchesOptions, selectedBatches]
  );

  const getMaxQuantity = useMemo(
    () => (batchNo: string) => {
      return allBatchesOptions.find((itm) => itm.name === batchNo)?.price.split(' ')[0];
    },
    [allBatchesOptions]
  );

  const updateQuantityReturned = (
    value: string,
    batchNo: string,
    maxQuantity: string | undefined
  ) => {
    const numericValue = Number(value);
    if (isNaN(numericValue) || numericValue < 0) {
      setErrors((prev) => ({
        ...prev,
        [batchNo]: 'Must be a positive number'
      }));
      return;
    }

    const max = Number(maxQuantity || 0);
    if (max && numericValue > max) {
      setErrors((prev) => ({
        ...prev,
        [batchNo]: `Exceeds maximum allowed (${max})`
      }));
      return;
    }

    setInputValues((prev) => ({
      ...prev,
      [batchNo]: value
    }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[batchNo];
      return next;
    });
    const index = selectedBatches.findIndex((b) => b.batch_no === batchNo);
    updateReturnedItem(index, { quantityReturned: numericValue });
  };

  return (
    <div className='mx-8 space-y-4'>
      {selectedBatches.map(({ batch_no }, index) => {
        return (
          <div key={index}>
            <div className='my-2 text-lg font-semibold'>
              {item.ingredient.name}
              <div className='bg-[#DCDBD5] inline-block mx-2 w-[8px] h-[8px] rounded-full' />
              {item.vendor.name}
            </div>
            <div
              className={`flex items-center  ${
                allBatchesOptions.length === 0 && 'opacity-50 cursor-not-allowed'
              } gap-2`}>
              <div className='grid grid-cols-2 gap-4'>
                <div className='w-full'>
                  <SelectDropdown
                    label='Batch'
                    placeholder='Select batch'
                    value={batch_no}
                    setValue={(val) => {
                      updateReturnedItem(index, { batch_no: val.name, id: val.id });
                    }}
                    options={getFilteredOptions(index)}
                  />
                </div>
                <CurrencyInput
                  label='Quantity returned'
                  placeholder='Quantity returned'
                  name={`quantity_returned_${batch_no}`}
                  hasUnit
                  unitValue={item?.ingredient?.unit_type}
                  disableUnit
                  hideCurrency
                  explicitRender
                  value={inputValues[batch_no] || ''}
                  onWheel={(e) => e.currentTarget.blur()}
                  error={errors[batch_no] || ''}
                  onChange={(val) =>
                    updateQuantityReturned(val, batch_no, getMaxQuantity(batch_no))
                  }
                />
                <div className='mt-[-12px] '>
                  <p className='text-blue-5 text-sm'>Maximum Quantity:{getMaxQuantity(batch_no)}</p>
                </div>
              </div>
              {selectedBatches.length > 1 && (
                <button
                  type='button'
                  onClick={() => removeBatch(index)}
                  className='justify-self-end'>
                  <DeleteIcon className='text-red-5 cursor-pointer w-5 h-5' />
                </button>
              )}
            </div>
          </div>
        );
      })}
      {allBatchesOptions.length !== selectedBatches.length && (
        <button
          onClick={addNewBatch}
          className='  bg-blue-500  rounded hover:bg-blue-600 transition-colors text-blue-5'>
          + Add New Batch
        </button>
      )}
    </div>
  );
};

export default ProcurementRequestItemReturn;
